<template>
	<li
		class="episodes-item"
		role="tab"
		:class="{
			expanded: expanded && !isGridViewOnly,
			'expanded-season': expanded && isSeason,
		}"
		ref="episodeItem"
	>
		<div class="episodes-item__heading" v-if="isLoggedIn">
			<MarkAsSeenButton
				v-if="episode.content.isReleased"
				:titleId="episode.id"
				:titleObjectId="episode.objectId"
				:titleObjectType="ObjectType.ShowEpisode"
				:titleName="titleName"
				:seasonNumber="episode.content.seasonNumber"
				:episodeNumber="episode.content.episodeNumber"
				:isTitleSeen="!!episode.seenlistEntry?.createdAt"
				:showSeenProgress="showSeenProgress"
				:lightBackground="true"
				trackingAction="episode-list"
				:showDetails="showDetails"
			/>

			<h4 @click="toggle()" role="button">
				<span>{{
					$t('WEBAPP_SEASON_X_EPISODE_Y', {
						season: episode.content.seasonNumber,
						episode: episode.content.episodeNumber,
					})
				}}</span>
				- <span class="episodes-item__heading--title">{{ episode.content.title }}</span>
			</h4>

			<div class="episodes-item__action">
				<button class="expand-btn expand-btn__exp" role="button" @click.stop="toggle()">
					<FontAwesomeIcon class="arrow-icon" :icon="expanded ? faChevronUp : faChevronDown" />
				</button>
			</div>
		</div>
		<div class="episodes-item__heading" @click="toggle()" v-else>
			<h4 role="button">
				<span>{{
					$t('WEBAPP_SEASON_X_EPISODE_Y', {
						season: episode.content.seasonNumber,
						episode: episode.content.episodeNumber,
					})
				}}</span>
				- <span class="episodes-item__heading--title">{{ episode.content.title }}</span>
			</h4>

			<button class="expand-btn" @click.stop="toggle('button')">
				<FontAwesomeIcon :icon="expanded ? faChevronUp : faChevronDown" />
			</button>
		</div>

		<div v-if="expanded" class="episodes-item__buy-box">
			<UpcomingContent
				v-if="isUpcoming && !showNewBuybox"
				:title="episode"
				:upcomingEpisodeReleases="upcomingEpisodeReleases"
				:parentTitle="title"
				:isTitleInWatchlist="isTitleInWatchlist"
				:additionalContexts="additionalContexts"
			>
				<template #buybox-switcher>
					<BuyBoxTypeSwitcher
						:title="title"
						class="buybox-switcher"
						:additionalContexts="additionalContexts"
						:buyBoxType="buyBoxType"
						@buyBoxTypeSwitched="handleBuyBoxTypeChange"
					/>
				</template>
			</UpcomingContent>
			<GridBuyBox
				v-else-if="!showNewBuybox"
				:title="title"
				:titleId="episode.id"
				:titleObjectId="episode.objectId"
				:titleObjectType="ObjectType.ShowEpisode"
				:titleName="episode.content.title"
				:seasonNumber="episode.content.seasonNumber"
				:episodeNumber="episode.content.episodeNumber"
				:additionalContexts="additionalContexts"
				:isTitleInWatchlist="isTitleInWatchlist"
				:showDetails="showDetails"
				:sponsoredAd="sponsoredAd"
				:isEpisodeBuybox="true"
				placement="episode_buybox"
				showFilter
			>
				<!-- isEpisodeBuybox above triggers filter state to be contained by each EP buybox, bypassing store state -->
				<template #buybox-title v-if="!isGridViewOnly">
					{{
						$t('WEBAPP_WATCH_SEASON_EPISODE_NOW', {
							seasonCount: episode.content.seasonNumber,
							episodeCount: episode.content.episodeNumber,
						})
					}}
				</template>
				<template #buybox-switcher>
					<BuyBoxTypeSwitcher
						:title="title"
						v-if="!isGridViewOnly"
						class="buybox-switcher"
						:additionalContexts="additionalContexts"
						:buyBoxType="buyBoxType"
						@buyBoxTypeSwitched="handleBuyBoxTypeChange"
					/>
				</template>
			</GridBuyBox>
			<ListBuyBox
				v-else-if="!isGridViewOnly"
				:title="title"
				:episode="episode"
				:sponsoredAd="sponsoredAd"
				:additionalContexts="additionalContexts"
				:isTitleInWatchlist="isTitleInWatchlist"
				:hideBuyBoxSwitcher="true"
				:isEpisodeBuybox="true"
				:episodeNumber="episode.content.episodeNumber"
				:upcomingEpisodeReleases="upcomingEpisodeReleases"
				placement="episode_buybox"
			>
				<template #buybox-title>
					{{
						$t('WEBAPP_WATCH_SEASON_EPISODE_NOW', {
							seasonCount: episode.content.seasonNumber,
							episodeCount: episode.content.episodeNumber,
						})
					}}
				</template>
				<!-- Episode buybox switcher is for individual episode buybox -->
				<template #buybox-switcher>
					<BuyBoxTypeSwitcher
						:title="title"
						class="buybox-switcher"
						:additionalContexts="additionalContexts"
						:buyBoxType="buyBoxType"
						@buyBoxTypeSwitched="handleBuyBoxTypeChange"
					/>
				</template>
			</ListBuyBox>

			<p style="white-space: pre-wrap">{{ description }}</p>
			<slot name="sponsoredRecommendation" />
			<br />
		</div>
	</li>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineAsyncComponent } from 'vue';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ObjectType, UpcomingRelease } from '@/@types/graphql-types';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { EpisodeFragment } from '@/pages/graphql/fragments/Episode.fragment';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';
import MarkAsSeenButton from '@/components/list/title-card/MarkAsSeenButton/MarkAsSeenButton.vue';
import BuyBoxTypeSwitcher from '@/features/buybox/components/BuyBoxTypeSwitcher.vue';

// Dynamically import components that are only displayed after expanding
const ListBuyBox = defineAsyncComponent(() => import('@/features/buybox/components/list/ListBuyBox.vue'));
const GridBuyBox = defineAsyncComponent(() => import('@/features/buybox/components/grid/GridBuyBox.vue'));
const UpcomingContent = defineAsyncComponent(() => import('@/components/upcoming/UpcomingContent.vue'));

import { useProvideOfferEntityId } from '@/helpers/composables/useProvideClickoutPlacement';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
import { useBuyboxView } from '@/features/buybox/composables/useBuyboxView';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { useExperiment } from '@/libraries/experiment';

const props = withDefaults(
	defineProps<{
		title: TitleDetail;
		episode: EpisodeFragment;
		titleId: string;
		titleObjectId: number;
		titleName: string;
		isTitleInWatchlist?: boolean;
		showSeenProgress?: number;
		additionalContexts?: SnowplowContext[];
		sponsoredAd?: SponsoredAdFragment | undefined;
	}>(),
	{
		isTitleInWatchlist: false,
		showSeenProgress: 0,
		additionalContexts: () => [],
		sponsoredAd: undefined,
	}
);

useProvideOfferEntityId(props.episode.id);

const { t } = useI18n();
const { showOldTitlePage, isLoggedIn, buyboxExpView } = storeToRefs(useUserStore());

const expanded = ref(false);
const episodeItem = ref<HTMLLIElement | null>(null);

const showDetails = computed(() => ({
	id: props.titleId,
	title: props.titleName,
	objectId: props.titleObjectId,
	objectType: ObjectType.Show,
}));

const { activeVariantWithControlGroup: gridSwiperExpVariant } = useExperiment('EXP_GRID_SWIPER');
const { activeVariantWithControlGroup: mergeBuyRentExpVariant } = useExperiment('EXP_MERGE_BUY_RENT');

const { isInternationalOffersTitle } = useInternationalOffersStatus({
	title: () => props.title,
});

const isUpcoming = computed(() => {
	return !props.episode.content.isReleased && props.episode.content.upcomingReleases.length > 0;
});

const upcomingEpisodeReleases = computed(() => {
	return props.episode.content.upcomingReleases.filter(el => el.__typename == 'UpcomingRelease') as UpcomingRelease[];
});

const description = computed(() => {
	return props.episode.content.shortDescription || t('WEBAPP_NO_DESCRIPTION');
});

const isSeason = computed(() => props.title.__typename === 'Season');
// keeping this one as Next we will have a toggle that shows the old TitlePage + the old BB only
const isGridViewOnly = computed(() => showOldTitlePage.value);

const { isGridBuyboxDefault } = useBuyboxView({ objectType: () => props.title.objectType });

const initialBuyboxType = computed(() => {
	if (isGridViewOnly.value) {
		return NewBuyBoxView.GRID;
	}
	if (isInternationalOffersTitle.value) return NewBuyBoxView.LIST;
	if (isGridBuyboxDefault.value) return buyboxExpView.value;

	return NewBuyBoxView.LIST;
});

const buyBoxType = ref(initialBuyboxType.value);

watch(isGridViewOnly, value => {
	buyBoxType.value = value ? NewBuyBoxView.GRID : NewBuyBoxView.LIST;
});

// Grid Swiper Exp
// We initialise the episode buybox type to match the Regular bbox type.
// If the user is in the grid swiper experiment and buybox is set to list - we switch the regular bbox type after exp activation.
// This means we need to update the episode bbox type to match regular.
watch(gridSwiperExpVariant, variant => {
	if (!variant || isGridViewOnly.value || isInternationalOffersTitle.value) return;

	buyBoxType.value = NewBuyBoxView.GRID;
});
// Grid Swiper Exp

// Merge Buy Rent Exp
watch(mergeBuyRentExpVariant, variant => {
	if (!variant || isGridViewOnly.value) return;

	buyBoxType.value = NewBuyBoxView.LIST;
});

const showNewBuybox = computed(() => {
	if (isGridViewOnly.value) {
		return false;
	}
	return buyBoxType.value === NewBuyBoxView.LIST;
});

function handleBuyBoxTypeChange(payload: { buyBoxType: NewBuyBoxView }) {
	buyBoxType.value = payload.buyBoxType;
}

async function toggle(location?: 'button') {
	// INP case: artificially give the JS thread time to breathe by adding a re-render.
	await new Promise(resolve => setTimeout(resolve, 0));
	expanded.value = !expanded.value;

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'toggle_episode',
			label: props.episode.content.episodeNumber + '',
			property: location,
			value: expanded.value ? 1 : 0,
		},
		[
			new SnowplowTitleContextGraphql(
				props.titleObjectId,
				ObjectType.Show,
				props.episode.content.seasonNumber,
				props.episode.content.episodeNumber
			),
		]
	);

	if (expanded.value) {
		episodeItem.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}
}
</script>
<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.expanded {
	border-bottom: 1px solid $jw-color-gold;
	.episodes-item__heading {
		color: $jw-color-gold;
		border-bottom: none;
		border-top: 1px solid $jw-color-gold;

		&--title {
			color: $jw-color-gold;
			font-weight: normal;
		}
	}
	:deep(.buybox-container__header__text) {
		margin-block: 0;
	}
}
.expand-btn {
	background-color: $jw-color-primary-dark--lighter;
	color: #c6c8cd;
	outline: none;
	border: none;
	height: 40px;
	width: 40px;
	min-width: 40px;
	min-height: 40px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	// TITLE_DETAIL_SHAOW_PAGE_LOGGED_IN_EXP
	&__exp {
		background-color: rgba(37, 41, 46, 0.7);
		height: 32px;
		width: 32px;
		min-width: 32px;
		min-height: 32px;
	}
	// TITLE_DETAIL_SHAOW_PAGE_LOGGED_IN_EXP
}

.episodes-item {
	cursor: pointer;

	&__action {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 16px;
		.divider {
			width: 1px;
			height: 16px;
			background-color: rgba(255, 255, 255, 0.08);
		}
	}

	&__buy-box {
		display: flex;
		flex-direction: column;
		:deep(.buybox .buybox__heading) {
			margin-top: 8px;
		}
	}
	&__heading {
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-bottom: 1px solid $jw-divider-color;
		gap: 8px;
		font-weight: normal;

		h4 {
			display: flex;
			font-size: 16px;
			flex-grow: 1;
			gap: 4px;

			span:first-child {
				white-space: nowrap;
			}
		}

		&--title {
			color: $jw-color-lightblue;
		}
	}

	.seen-icon-btn {
		background-color: $jw-card-grey;
		height: 32px;
		width: 32px;
		border-radius: 50%;
	}

	.seen-icon {
		color: $btn-mark-as-seen-color-grey;

		&--seen {
			color: $jw-color-green;
		}
	}

	// TITLE_DETAIL_SHAOW_PAGE_LOGGED_IN_EXP
	.mark-as-seen-button {
		&.sm {
			min-height: 24px;
			height: 24px;
			min-width: 24px;
			width: 24px;
		}
	}
	// TITLE_DETAIL_SHAOW_PAGE_LOGGED_IN_EXP
}

.expanded-season {
	border-top: 1px solid $jw-color-gold;
	border-bottom: 1px solid $jw-color-gold;

	h4,
	.episodes-item__heading {
		color: $jw-color-gold;
		font-weight: normal;
		border-bottom: none;

		&--title {
			color: $jw-color-gold;
		}
	}

	:deep(.buybox-container__header__text) {
		margin-block: 0;
		margin-top: 0;
	}
}
</style>
